/**
 * The board's spine: pages → tabs → features, biggest first.
 *
 * ── Why the shape changed ────────────────────────────────────────────────────────────────────
 * The board used to be a stack of whatever had been built: candidate requests at the top because
 * it shipped first, then sent emails, then outcomes, then — five screens down — "most used views",
 * which is the question the page is actually opened with. Reading it meant scrolling past four
 * answers to reach the first question. This turns it the other way up: which pages get used, then
 * which of their tabs, then what people do inside them, with everything below the first level
 * closed until someone asks for it.
 *
 * Pure on purpose — the tree is where "what does this number mean" is decided, and it is worth
 * being able to test that without a browser.
 */
import type { UsageSummary } from '../../services/usageApi';
import { describeEvent } from './eventDescriptions';
import { VIEW_ALIASES, pageMeta, tabLabel, type PageMeta } from './pageRegistry';

/** One instrumented action inside a tab. */
export interface FeatureNode {
  event: string;
  count: number;
  /** The one-line explanation, or null when the event has none yet. */
  description: string | null;
}

export interface TabNode {
  /** The section key as recorded, e.g. `ta-action-boards/feedback`. */
  section: string;
  /** Tab slug alone, or `unattributed`. */
  tab: string;
  label: string;
  total: number;
  /** Distinct people, or null when the proxy predates per-tab user counts. */
  users: number | null;
  features: FeatureNode[];
  /**
   * Sub-tabs, where a tab has them — the TA Cockpit's Tracking splits into Delivery and Hiring,
   * Role Prioritisation into the score and the check-in. Empty for a tab that has none.
   */
  subTabs: TabNode[];
}

export interface PageNode extends PageMeta {
  total: number;
  users: number | null;
  /** Tabs, biggest first. Empty for a page that has none. */
  tabs: TabNode[];
  /** Events recorded on the page itself, outside any tab. */
  features: FeatureNode[];
  /** Share of all events in the range — the denominator a raw count is missing. */
  share: number;
}

/** The section the server files events it cannot place under. Never a real page or tab. */
export const UNATTRIBUTED = 'unattributed';

/**
 * Fold the section rollup into pages and tabs.
 *
 * The API hands back one flat list of `page/tab` sections; splitting it here rather than serving
 * it pre-nested keeps the wire format boring and means a UI question ("should the Log and /log be
 * one row?") is answered in the UI.
 */
export function buildPageTree(summary: UsageSummary): PageNode[] {
  const sections = summary.eventsBySection ?? [];
  const usersBySection = summary.usersBySection;
  const usersByView = summary.usersByView;
  const pages = new Map<string, PageNode>();

  /** Tab nodes by `view/tab`, so a sub-tab can find the tab it belongs under. */
  const tabs = new Map<string, TabNode>();

  const tabNode = (page: PageNode, view: string, tab: string): TabNode => {
    const section = `${view}/${tab}`;
    const existing = tabs.get(section);
    if (existing) return existing;
    const node: TabNode = {
      section,
      tab,
      label: tabLabel(view, tab),
      total: 0,
      users: usersBySection ? (usersBySection[section] ?? 0) : null,
      features: [],
      subTabs: [],
    };
    tabs.set(section, node);
    page.tabs.push(node);
    return node;
  };

  for (const section of sections) {
    const [rawView, tab, sub] = splitSection(section.section);
    const view = VIEW_ALIASES[rawView] ?? rawView;
    if (!pages.has(view)) {
      pages.set(view, {
        ...pageMeta(view),
        total: 0,
        users: null,
        tabs: [],
        features: [],
        share: 0,
      });
    }
    const page = pages.get(view)!;
    page.total += section.total;

    const features = section.events.map((event) => ({
      event: event.name,
      count: event.count,
      description: describeEvent(event.name),
    }));

    if (!tab) {
      // Two sections can land here for one page — `/log` and `/interviews` are the same page under
      // two keys — so features are merged rather than replaced.
      page.features = mergeFeatures(page.features, features);
      continue;
    }

    const node = tabNode(page, view, tab);
    // A sub-tab's events count towards its tab as well: a tab's total has to be everything that
    // happened in it, or a page whose work all sits one level down would read as unused.
    node.total += section.total;

    if (!sub) {
      node.features = mergeFeatures(node.features, features);
      continue;
    }
    node.subTabs.push({
      section: section.section,
      tab: sub,
      label: tabLabel(view, sub),
      total: section.total,
      users: usersBySection ? (usersBySection[section.section] ?? 0) : null,
      features,
      subTabs: [],
    });
  }

  const total = summary.totals.events || 0;
  const bySize = (a: TabNode, b: TabNode) => b.total - a.total || a.label.localeCompare(b.label);
  for (const page of pages.values()) {
    page.tabs.sort(bySize);
    for (const tab of page.tabs) {
      tab.subTabs.sort(bySize);
      tab.features.sort((a, b) => b.count - a.count || a.event.localeCompare(b.event));
      for (const sub of tab.subTabs) {
        sub.features.sort((a, b) => b.count - a.count || a.event.localeCompare(b.event));
      }
    }
    page.features.sort((a, b) => b.count - a.count || a.event.localeCompare(b.event));
    page.share = total > 0 ? page.total / total : 0;
    if (usersByView) {
      // An alias is the same page under a second key, so its people are the same people counted
      // twice if the two figures are added. The larger of the two is the honest floor.
      const alternates = Object.entries(VIEW_ALIASES)
        .filter(([, canonical]) => canonical === page.key)
        .map(([alias]) => usersByView[alias] ?? 0);
      page.users = Math.max(usersByView[page.key] ?? 0, ...alternates, 0);
    }
  }

  return [...pages.values()].sort((a, b) => {
    // The unplaceable remainder is bookkeeping, not a page anyone used: last, whatever its size.
    if (a.key === UNATTRIBUTED) return 1;
    if (b.key === UNATTRIBUTED) return -1;
    return b.total - a.total || a.label.localeCompare(b.label);
  });
}

/**
 * `ta-cockpit/tracking/hiring` → `['ta-cockpit', 'tracking', 'hiring']`.
 *
 * Up to three parts, because that is as deep as `sectionKey` will ever go — and it only goes that
 * deep for views on its own allowlist.
 */
function splitSection(section: string): [string, string | null, string | null] {
  const [view, tab, sub] = section.split('/');
  return [view, tab || null, sub || null];
}

function mergeFeatures(a: FeatureNode[], b: FeatureNode[]): FeatureNode[] {
  const byName = new Map(a.map((feature) => [feature.event, { ...feature }]));
  for (const feature of b) {
    const existing = byName.get(feature.event);
    if (existing) existing.count += feature.count;
    else byName.set(feature.event, { ...feature });
  }
  return [...byName.values()];
}

export interface TreeFilter {
  /** A bold sidebar heading — a group or a subgroup — or null for all of them. */
  area: string | null;
  /** Free text matched against page, tab, event name and description. */
  query: string;
}

/**
 * Does this page sit under the chosen heading?
 *
 * Hierarchical, because the sidebar is: "Candidate Communication" sits inside "Talent
 * Acquisition", so picking the parent has to keep the five pages underneath it. Matching the
 * narrowest heading alone would make choosing Talent Acquisition HIDE the TA Action Boards, which
 * is the opposite of what picking a section means.
 */
function inArea(page: PageNode, area: string): boolean {
  return page.area === area || page.parentArea === area;
}

/** The headings worth offering: the ones that actually hold a page here, with how many. */
export function areasInTree(
  tree: PageNode[],
  headings: { name: string; parent: string | null }[]
): { name: string; parent: string | null; pages: number }[] {
  return headings
    .map((heading) => ({
      ...heading,
      pages: tree.filter((page) => inArea(page, heading.name)).length,
    }))
    .filter((heading) => heading.pages > 0);
}

/**
 * Narrow the tree, keeping a page whose page-level label matches WHOLE.
 *
 * Searching "requests" should show the Requests tab and what happens on it — but searching "TA
 * Action Boards" should show the board intact, not an empty shell whose tabs all failed to match
 * the page's own name. So a match at one level keeps everything under it, and a match deeper down
 * keeps the branch that leads to it.
 */
export function filterPageTree(tree: PageNode[], { area, query }: TreeFilter): PageNode[] {
  const needle = query.trim().toLowerCase();
  const out: PageNode[] = [];

  for (const page of tree) {
    if (area && !inArea(page, area)) continue;
    if (!needle) {
      out.push(page);
      continue;
    }
    if (matches(page.label, needle) || matches(page.key, needle)) {
      out.push(page);
      continue;
    }
    const tabs = page.tabs.map((tab) => filterTab(tab, needle)).filter((tab): tab is TabNode => tab !== null);
    const features = page.features.filter((feature) => featureMatches(feature, needle));
    if (tabs.length > 0 || features.length > 0) out.push({ ...page, tabs, features });
  }

  return out;
}

/**
 * A tab kept whole when its own name matches, otherwise narrowed to what does — and the same
 * question asked of its sub-tabs, so searching "hiring" finds the Cockpit's Hiring view rather
 * than only the events inside it.
 */
function filterTab(tab: TabNode, needle: string): TabNode | null {
  if (matches(tab.label, needle)) return tab;
  const subTabs = tab.subTabs.map((sub) => filterTab(sub, needle)).filter((sub): sub is TabNode => sub !== null);
  const features = tab.features.filter((feature) => featureMatches(feature, needle));
  if (subTabs.length === 0 && features.length === 0) return null;
  return { ...tab, subTabs, features };
}

function matches(value: string, needle: string): boolean {
  return value.toLowerCase().includes(needle);
}

function featureMatches(feature: FeatureNode, needle: string): boolean {
  return matches(feature.event, needle) || matches(feature.description ?? '', needle);
}

/** Every page id in the tree — what "expand everything" expands. */
export function allPageKeys(tree: PageNode[]): string[] {
  return tree.map((page) => page.key);
}
